import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Activity, RefreshCw, Eye, Users, TimerReset, MousePointerClick, Globe, ShoppingBag } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "../../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const RANGES = [7, 30, 90];

function fmtDuration(secs) {
  if (!secs) return "0s";
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function fmtDay(d) {
  return new Date(`${d}T12:00:00`).toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}

function fmtHour(h) {
  if (h === 0) return "12am";
  if (h === 12) return "12pm";
  return h < 12 ? `${h}am` : `${h - 12}pm`;
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="border border-[#DDD7CF] rounded-lg bg-white p-5" data-testid={testid}>
      <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#6E675F] flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 text-[#8C7B6B]" /> {label}
      </p>
      <p className="text-2xl font-medium text-[#1A1A1A] mt-1.5">{value}</p>
      {sub && <p className="text-xs text-[#6E675F] mt-1">{sub}</p>}
    </div>
  );
}

const tooltipStyle = { border: "1px solid #DDD7CF", borderRadius: 8, fontSize: 12, background: "#fff" };

export const AdminAnalytics = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/analytics?days=${days}`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load analytics"))
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { totals, daily, hourly, sources, top_pages, top_products } = data;
  const chartData = daily.map((d) => ({ ...d, label: fmtDay(d.day) }));
  const hourData = hourly.map((h) => ({ ...h, label: fmtHour(h.hour) }));

  return (
    <div data-testid="admin-analytics-section">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] flex items-center gap-2">
          <Activity className="w-4 h-4" /> Site Traffic
        </p>
        <div className="flex items-center gap-2 ml-auto">
          {RANGES.map((r) => (
            <button
              key={r}
              data-testid={`analytics-range-${r}`}
              onClick={() => setDays(r)}
              className={`text-xs tracking-[0.15em] uppercase font-semibold px-4 py-2 rounded-none border transition-colors ${
                days === r ? "bg-[#8C7B6B] text-white border-[#8C7B6B]" : "border-[#DDD7CF] text-[#6E675F] hover:border-[#8C7B6B]"
              }`}
            >
              {r}d
            </button>
          ))}
          <button onClick={load} data-testid="analytics-refresh-button" className="text-xs text-[#8C7B6B] font-semibold inline-flex items-center gap-1 hover:text-[#1A1A1A] transition-colors ml-2">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {totals.views === 0 && (
        <div className="border border-dashed border-[#DDD7CF] rounded-lg bg-[#EFEBE6]/50 px-6 py-4 mb-6 text-sm text-[#6E675F]" data-testid="analytics-empty-note">
          Tracking just started — numbers appear here as visitors browse the store. Admin pages are never counted.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-8" data-testid="analytics-stat-grid">
        <StatCard icon={Users} label="Unique Visitors" value={totals.visitors.toLocaleString()} sub={`${totals.sessions.toLocaleString()} visit${totals.sessions === 1 ? "" : "s"}`} testid="analytics-visitors" />
        <StatCard icon={Eye} label="Page Views" value={totals.views.toLocaleString()} sub={`${totals.avg_pages_per_session} pages / visit`} testid="analytics-views" />
        <StatCard icon={ShoppingBag} label="Conversion Rate" value={totals.conversion_rate === null ? "—" : `${totals.conversion_rate}%`} sub={totals.conversion_rate === null ? "tracking too new to compare" : `${totals.orders} order${totals.orders === 1 ? "" : "s"} / ${totals.visitors} visitor${totals.visitors === 1 ? "" : "s"}`} testid="analytics-conversion" />
        <StatCard icon={MousePointerClick} label="Bounce Rate" value={`${totals.bounce_rate}%`} sub="left after one page" testid="analytics-bounce" />
        <StatCard icon={TimerReset} label="Avg Visit Time" value={fmtDuration(totals.avg_session_seconds)} sub="multi-page visits" testid="analytics-duration" />
        <StatCard icon={Activity} label="Views / Day" value={(totals.views / data.days).toFixed(1)} sub={`over ${data.days} days`} testid="analytics-views-per-day" />
      </div>

      <div className="border border-[#DDD7CF] rounded-lg bg-white p-6 mb-6" data-testid="analytics-daily-chart">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] mb-4">Daily Traffic</p>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={chartData} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
            <defs>
              <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8C7B6B" stopOpacity={0.35} />
                <stop offset="95%" stopColor="#8C7B6B" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#EFEBE6" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6E675F" }} interval="preserveStartEnd" tickLine={false} axisLine={{ stroke: "#DDD7CF" }} />
            <YAxis tick={{ fontSize: 11, fill: "#6E675F" }} allowDecimals={false} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Area type="monotone" dataKey="views" name="Page views" stroke="#8C7B6B" strokeWidth={2} fill="url(#gViews)" />
            <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#1A1A1A" strokeWidth={1.5} fill="none" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="border border-[#DDD7CF] rounded-lg bg-white p-6" data-testid="analytics-hourly-chart">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] mb-4">Busiest Times (Toronto time)</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={hourData} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EFEBE6" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6E675F" }} interval={2} tickLine={false} axisLine={{ stroke: "#DDD7CF" }} />
              <YAxis tick={{ fontSize: 11, fill: "#6E675F" }} allowDecimals={false} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#EFEBE6" }} />
              <Bar dataKey="views" name="Page views" fill="#8C7B6B" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-[#DDD7CF] rounded-lg bg-white p-6" data-testid="analytics-sources">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] mb-4 flex items-center gap-2">
            <Globe className="w-4 h-4" /> Traffic Sources
          </p>
          {sources.length === 0 ? (
            <p className="text-sm text-[#6E675F]" data-testid="analytics-no-sources">No visits recorded yet.</p>
          ) : (
            <div className="space-y-2.5">
              {sources.map((s) => (
                <div key={s.source} className="flex items-center gap-3" data-testid={`analytics-source-row-${s.source}`}>
                  <span className="text-sm text-[#1A1A1A] font-medium truncate flex-1">{s.source}</span>
                  <div className="w-28 h-2 rounded-none bg-[#EFEBE6] overflow-hidden shrink-0">
                    <div className="h-full bg-[#8C7B6B]" style={{ width: `${Math.max(6, (s.sessions / sources[0].sessions) * 100)}%` }} />
                  </div>
                  <span className="text-xs text-[#6E675F] w-24 text-right shrink-0">{s.sessions} visit{s.sessions === 1 ? "" : "s"} · {s.pct}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="border border-[#DDD7CF] rounded-lg bg-white p-6" data-testid="analytics-top-pages">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] mb-4">Top Pages</p>
          {top_pages.length === 0 ? (
            <p className="text-sm text-[#6E675F]" data-testid="analytics-no-pages">No page views yet.</p>
          ) : (
            <div className="space-y-2.5">
              {top_pages.map((p) => (
                <div key={p.path} className="flex items-center gap-3" data-testid={`analytics-page-row-${p.path}`}>
                  <span className="text-sm text-[#1A1A1A] font-medium truncate flex-1">{p.path}</span>
                  <div className="w-28 h-2 rounded-none bg-[#EFEBE6] overflow-hidden shrink-0">
                    <div className="h-full bg-[#8C7B6B]" style={{ width: `${Math.max(6, (p.views / top_pages[0].views) * 100)}%` }} />
                  </div>
                  <span className="text-xs text-[#6E675F] w-20 text-right shrink-0">{p.views} views · {p.visitors}v</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border border-[#DDD7CF] rounded-lg bg-white p-6" data-testid="analytics-top-products">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] mb-4">Most Viewed Products</p>
        {top_products.length === 0 ? (
          <p className="text-sm text-[#6E675F]" data-testid="analytics-no-products">No product page views yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead className="text-right">Page Views</TableHead>
                <TableHead className="text-right">Units Sold ({data.days}d)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {top_products.map((p) => (
                <TableRow key={p.product_id} data-testid={`analytics-product-row-${p.product_id}`}>
                  <TableCell className="font-medium">{p.name} <span className="text-[#6E675F] text-xs">{p.dosage}</span></TableCell>
                  <TableCell className="text-right">{p.views}</TableCell>
                  <TableCell className="text-right">{p.units_sold}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        </div>
      </div>
    </div>
  );
};
